/**
 * The transport: one `send`, the four ways of authenticating it, and the error
 * every resource file throws. Nothing here knows a route.
 */
import { supabase } from "../supabase.ts";

const API_URL = import.meta.env.API_URL;

export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  constructor(
    readonly status: number,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = "ApiError";
  }

  /** True when the API could not be reached at all. */
  get isUnreachable(): boolean {
    return this.status === 0;
  }
}

/**
 * Calls the ondo API as the signed-in user.
 *
 * The token is read from the live session on every call rather than cached:
 * supabase-js refreshes it in the background, and a stale copy would start
 * failing after an hour.
 */
export async function request<T>(path: string, body?: unknown): Promise<T> {
  if (body === undefined) return send<T>(path, await authHeaders());

  return send<T>(path, await authHeaders(true), JSON.stringify(body));
}

/**
 * The caller's own credentials, read fresh.
 *
 * Split out of `request` because two calls need a verb it does not offer — a
 * handle is PUT and a room is DELETEd — and reimplementing the token read at
 * each of them is how one of them comes to cache it.
 */
async function authHeaders(json = false): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  if (!token) throw new ApiError(401, "Not signed in.");

  return json
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { Authorization: `Bearer ${token}` };
}

/** An authenticated call that is not a GET or a POST. */
export async function requestWith<T>(
  method: "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  return body === undefined
    ? send<T>(path, await authHeaders(), undefined, method)
    : send<T>(path, await authHeaders(true), JSON.stringify(body), method);
}

/** Bytes the server built, and the name it gave them. */
export interface ApiFile {
  blob: Blob;
  /**
   * From `Content-Disposition`, or null when the server did not name it — the
   * header is only readable because the route sends
   * `Access-Control-Expose-Headers`, and a route that forgets to is a missing
   * name rather than a failed download.
   */
  filename: string | null;
}

/**
 * Reads BYTES from the ondo API as the signed-in user.
 *
 * `send` parses every response as JSON, which is right for every route that
 * existed before OND-67 and wrong for the two that now answer with a file: the
 * server-built collector-list export, and a work photograph stamped with the
 * reader's own name.
 *
 * AN `<img src>` CANNOT CARRY A BEARER TOKEN, which is why the image goes
 * through here and becomes an object URL rather than being pointed at
 * directly. The alternative — a credential in a query string — is a URL that
 * can be copied out of the DOM and shared, and the whole point of that route
 * is that the bytes are bound to one reader.
 */
export async function requestFile(path: string): Promise<ApiFile> {
  const headers = await authHeaders();

  let response: Response;
  try {
    response = await fetch(`${API_URL}/v1${path}`, { headers });
  } catch (cause) {
    throw new ApiError(
      0,
      `Could not reach the ONDO API at ${API_URL}. Is it running?`,
      { cause },
    );
  }

  if (!response.ok) {
    // A refusal is still JSON — the server's own sentence — even on a route
    // that answers with bytes when it succeeds.
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(response.status, body?.message ?? response.statusText);
  }

  const disposition = response.headers.get("Content-Disposition") ?? "";
  const named = /filename="([^"]+)"/.exec(disposition);

  return { blob: await response.blob(), filename: named?.[1] ?? null };
}

/**
 * Calls the ondo API with no session.
 *
 * Only signup uses this, and only because it must: the caller has no account
 * yet, which is the whole point of the endpoint. Everything else goes through
 * `request` — an unauthenticated call is a decision, not a default.
 */
export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  return send<T>(path, { "Content-Type": "application/json" }, JSON.stringify(body));
}

/**
 * Reads from the ondo API with no session.
 *
 * The second deliberate unauthenticated path after signup, and the first that
 * READS. It exists for exactly one address — a viewing room's cover, which has
 * to answer a caller who has no account, because that is the whole point of the
 * link. Everything else goes through `request`.
 *
 * Note the empty header set rather than a Content-Type: `send` infers GET from
 * an absent body, and a GET carrying a content type describes a body it does
 * not have.
 */
export async function publicGet<T>(path: string): Promise<T> {
  return send<T>(path, {});
}

export async function send<T>(
  path: string,
  headers: Record<string, string>,
  body?: string,
  method?: "POST" | "PUT" | "PATCH" | "DELETE",
): Promise<T> {
  // Built conditionally rather than passing `body: undefined`: with
  // exactOptionalPropertyTypes an explicit undefined is not the same as an
  // absent key, and RequestInit does not accept one.
  //
  // The method is still INFERRED where it is not given — no body is a GET, a
  // body is a POST — because that is what every call written before viewing
  // rooms relies on. What it can no longer do is turn a bodyless DELETE into a
  // GET, which is why the verb wins when one is named.
  const init: RequestInit =
    body === undefined
      ? method === undefined
        ? { headers }
        : { method, headers }
      : { method: method ?? "POST", headers, body };

  let response: Response;
  try {
    response = await fetch(`${API_URL}/v1${path}`, init);
  } catch (cause) {
    // fetch rejects with a bare TypeError("Failed to fetch") for every
    // network-level failure — server down, DNS, CORS, TLS. That message tells
    // the reader nothing, and the most common cause in development is simply
    // that the API is not running. Name the address so it is self-diagnosing.
    throw new ApiError(
      0,
      `Could not reach the ONDO API at ${API_URL}. Is it running?`,
      { cause },
    );
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(response.status, body?.message ?? response.statusText);
  }

  // An empty body is a real answer, not a malformed one.
  //
  // Every route that existed before provenance returned something, so this read
  // `await response.json()` and nothing ever noticed. The provenance writes
  // return `void`, which Nest answers 201 with no body at all — and
  // `response.json()` on that throws a SyntaxError. Being a SyntaxError rather
  // than an ApiError, it defeated every `instanceof ApiError` check downstream:
  // the submit failed with "Something went wrong" AFTER the write had succeeded,
  // and the retry hit the database's own uniqueness refusal.
  const text = await response.text();
  return (text === "" ? undefined : JSON.parse(text)) as T;
}
